import { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../api'
import { APP_DEFAULTS, GEMINI_MODEL_CANDIDATES } from '../defaults'

// KBNL-118: '이관 도구'(로컬 → GCS/Firestore 마이그레이션) 섹션 숨김 — 초기
//   전환기 도구로 현재는 불필요 (사용자 요청). 코드는 보존, 재노출 시 true.
const SHOW_MIGRATION_TOOLS = false

// 단일 glossary_id (구버전) → glossary_ids 배열 정규화
const normalizeGlossaryIds = (s) => {
  if (Array.isArray(s?.glossary_ids) && s.glossary_ids.length > 0) return [...s.glossary_ids]
  if (s?.glossary_id) return [s.glossary_id]
  return []
}

// Gemini 모델 후보는 ui/src/defaults.js 의 GEMINI_MODEL_CANDIDATES 로 중앙화 —
// 새 모델 출시 시 그 파일만 갱신. 자유 입력도 허용되므로 목록 밖 모델도
// 직접 입력해 '✓ 확인' 으로 검증 가능.

export default function MetaSettings({ settings, isAdmin, virtualSchemaEnabled, onSaveUser, onSaveDefault, onClose, showToast }) {
  const [mode, setMode] = useState('user')  // 'user' | 'default'
  const [form, setForm] = useState(() => ({ ...settings, glossary_ids: normalizeGlossaryIds(settings) }))
  const [isMigrating, setIsMigrating] = useState(false)
  const [migrateResult, setMigrateResult] = useState(null)
  // Gemini 모델 사용 가능 확인 — 단건(result) / 전체(all)
  const [modelCheck, setModelCheck] = useState({ checking: false, checkingAll: false, result: null, all: null })

  // 실호출과 동일한 리전으로 검증 — LLM 호출부의 location 결정 로직과 일치
  // (vertex_location 우선). 리전별 가용성이 달라 다른 리전 검증은 오판을 낳는다.
  const checkLocation = () => (form.vertex_location || form.location || APP_DEFAULTS.location || '')

  const runModelCheck = async (model) => {
    setModelCheck(s => ({ ...s, checking: true, result: null, all: null }))
    try {
      const r = await api.modelCheck('gemini', (model || '').trim(), checkLocation())
      setModelCheck(s => ({ ...s, checking: false, result: r }))
    } catch (e) {
      setModelCheck(s => ({ ...s, checking: false, result: { ok: false, model, error: e.message } }))
    }
  }

  const runModelCheckAll = async () => {
    setModelCheck(s => ({ ...s, checkingAll: true, result: null, all: null }))
    const rs = await Promise.all(GEMINI_MODEL_CANDIDATES.map(m =>
      api.modelCheck('gemini', m, checkLocation()).catch(e => ({ ok: false, model: m, error: e.message }))))
    setModelCheck(s => ({ ...s, checkingAll: false, all: rs }))
  }

  const [fsDatabases, setFsDatabases] = useState([])
  const [fsCollections, setFsCollections] = useState([])
  const [loadingFsDb, setLoadingFsDb] = useState(false)
  const [loadingFsColl, setLoadingFsColl] = useState(false)

  const [selectedTables, setSelectedTables] = useState(() => {
    if (settings.selected_tables && Object.keys(settings.selected_tables).length > 0) {
      return { ...settings.selected_tables }
    }
    if (settings.dataset_id && settings.table_ids?.length) {
      return { [settings.dataset_id]: [...settings.table_ids] }
    }
    return {}
  })

  const [datasets, setDatasets] = useState([])
  const [dsTableCache, setDsTableCache] = useState({})
  const [glossaries, setGlossaries] = useState([])
  const [loadingDs, setLoadingDs] = useState(false)
  const [loadingTables, setLoadingTables] = useState({})
  const [loadingGl, setLoadingGl] = useState(false)
  const [expandedDs, setExpandedDs] = useState(() => new Set(Object.keys(settings.selected_tables || {})))

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // KBNL-57: Virtual Schema 목록 로드 + 선택 토글 (flag enabled 일 때만)
  const [vsList, setVsList] = useState([])
  const [vsLoading, setVsLoading] = useState(false)
  const [vsQuery, setVsQuery] = useState('')

  useEffect(() => {
    if (virtualSchemaEnabled === false) return
    let cancelled = false
    setVsLoading(true)
    api.listVirtualSchemas()
      .then(res => { if (!cancelled) setVsList(res?.items || []) })
      .catch(() => { if (!cancelled) setVsList([]) })
      .finally(() => { if (!cancelled) setVsLoading(false) })
    return () => { cancelled = true }
  }, [virtualSchemaEnabled])

  const toggleVirtualSchema = useCallback((docId) => {
    setForm(f => {
      const cur = Array.isArray(f.selected_virtual_schema_ids) ? f.selected_virtual_schema_ids : []
      const next = cur.includes(docId) ? cur.filter(x => x !== docId) : [...cur, docId]
      return { ...f, selected_virtual_schema_ids: next }
    })
  }, [])

  const filteredVsList = useMemo(() => {
    const q = (vsQuery || '').trim().toLowerCase()
    if (!q) return vsList
    return vsList.filter(v => {
      const name = (v.name || '').toLowerCase()
      const desc = (v.description || '').toLowerCase()
      const owner = (v.owner_name || v.owner_user_id || '').toLowerCase()
      return name.includes(q) || desc.includes(q) || owner.includes(q)
    })
  }, [vsList, vsQuery])

  const myUserId = settings?.user_id || ''

  const fetchDatasets = useCallback(async () => {
    if (!form.project_id) return
    setLoadingDs(true)
    try {
      const list = await api.listDatasets(form.project_id, form.location)
      setDatasets(list)
    } catch { setDatasets([]) }
    finally { setLoadingDs(false) }
  }, [form.project_id, form.location])

  const refreshDatasets = useCallback(() => {
    setDsTableCache({})  // 테이블 캐시도 함께 무효화
    fetchDatasets()
  }, [fetchDatasets])

  const fetchTablesFor = useCallback(async (dsId) => {
    if (!form.project_id || !dsId) return
    if (dsTableCache[dsId]) return
    setLoadingTables(prev => ({ ...prev, [dsId]: true }))
    try {
      const list = await api.listTables(form.project_id, dsId)
      setDsTableCache(prev => ({ ...prev, [dsId]: list }))
    } catch {
      setDsTableCache(prev => ({ ...prev, [dsId]: [] }))
    }
    finally { setLoadingTables(prev => ({ ...prev, [dsId]: false })) }
  }, [form.project_id, dsTableCache])

  const fetchGlossaries = useCallback(async () => {
    if (!form.project_id) return
    setLoadingGl(true)
    try {
      const list = await api.listGlossaries(form.project_id, form.location)
      setGlossaries(list)
    } catch { setGlossaries([]) }
    finally { setLoadingGl(false) }
  }, [form.project_id, form.location])

  useEffect(() => {
    if (form.project_id) {
      fetchDatasets()
      fetchGlossaries()
    }
  }, [fetchDatasets, fetchGlossaries])

  useEffect(() => {
    Object.keys(selectedTables).forEach(dsId => {
      if (!dsTableCache[dsId]) fetchTablesFor(dsId)
    })
  }, [selectedTables, dsTableCache, fetchTablesFor])

  const fetchFsDatabases = useCallback(async () => {
    const pid = form.fs_project || form.project_id
    if (!pid) return
    setLoadingFsDb(true)
    try {
      const list = await api.listFirestoreDatabases(pid)
      setFsDatabases(list)
    } catch { setFsDatabases([]) }
    finally { setLoadingFsDb(false) }
  }, [form.fs_project, form.project_id])

  const fetchFsCollections = useCallback(async () => {
    const pid = form.fs_project || form.project_id
    const db = form.fs_database || APP_DEFAULTS.fs_database
    if (!pid) return
    setLoadingFsColl(true)
    try {
      const list = await api.listFirestoreCollections(pid, db)
      setFsCollections(list)
    } catch { setFsCollections([]) }
    finally { setLoadingFsColl(false) }
  }, [form.fs_project, form.project_id, form.fs_database])

  useEffect(() => {
    if (form.storage_type === 'firestore') {
      fetchFsDatabases()
      fetchFsCollections()
    }
  }, [form.storage_type, fetchFsDatabases, fetchFsCollections])

  const toggleDatasetExpand = (dsId) => {
    setExpandedDs(prev => {
      const next = new Set(prev)
      if (next.has(dsId)) next.delete(dsId); else next.add(dsId)
      return next
    })
    if (!dsTableCache[dsId]) fetchTablesFor(dsId)
  }

  const toggleTable = (dsId, tableId) => {
    setSelectedTables(prev => {
      const dsTables = new Set(prev[dsId] || [])
      if (dsTables.has(tableId)) dsTables.delete(tableId); else dsTables.add(tableId)
      const next = { ...prev }
      if (dsTables.size === 0) delete next[dsId]; else next[dsId] = [...dsTables]
      return next
    })
  }

  const toggleDatasetAll = (dsId) => {
    const allTables = dsTableCache[dsId] || []
    const current = selectedTables[dsId] || []
    setSelectedTables(prev => {
      const next = { ...prev }
      if (current.length === allTables.length) {
        delete next[dsId]
      } else {
        next[dsId] = [...allTables]
      }
      return next
    })
  }

  const totalSelected = Object.values(selectedTables).reduce((s, arr) => s + arr.length, 0)

  const handleSave = () => {
    const data = {
      ...form,
      selected_tables: selectedTables,
      table_ids: [],
      dataset_id: '',
      glossary_ids: Array.isArray(form.glossary_ids) ? form.glossary_ids : [],
      glossary_id: '',  // 다중 용어집으로 마이그레이션 — 단일 필드 비움
    }
    if (mode === 'default') {
      onSaveDefault(data)
    } else {
      onSaveUser(data)
    }
  }

  const toggleGlossary = (gid) => {
    setForm(f => {
      const cur = Array.isArray(f.glossary_ids) ? f.glossary_ids : []
      const next = cur.includes(gid) ? cur.filter(x => x !== gid) : [...cur, gid]
      return { ...f, glossary_ids: next }
    })
  }

  const switchMode = async (next) => {
    if (next === mode) return
    if (next === 'default') {
      try {
        const { settings: def, exists } = await api.getDefaultSettings()
        if (exists && def && Object.keys(def).length > 0) {
          setForm(f => ({ ...f, ...def, glossary_ids: normalizeGlossaryIds(def) }))
          if (def.selected_tables) setSelectedTables({ ...def.selected_tables })
        }
        setMode('default')
      } catch (e) {
        showToast?.('error', '공유 기본값 로드 실패: ' + e.message)
      }
    } else {
      setForm({ ...settings, glossary_ids: normalizeGlossaryIds(settings) })
      setSelectedTables({ ...(settings.selected_tables || {}) })
      setMode('user')
    }
  }

  const [resetting, setResetting] = useState(false)
  const handleResetDefaults = async () => {
    setResetting(true)
    try {
      const defaults = await api.getDefaults()
      setForm(prev => ({ ...prev, ...defaults, glossary_ids: normalizeGlossaryIds(defaults) }))
      setSelectedTables(defaults.selected_tables || {})
    } catch (e) {
      showToast?.('error', '기본값 로드 실패: ' + e.message)
    } finally {
      setResetting(false)
    }
  }

  const handleMigrate = async (overwrite) => {
    const st = form.storage_type
    let body
    if (st === 'gcs') {
      if (!form.gcs_path) return
      body = { target: 'gcs', gcs_path: form.gcs_path, overwrite }
    } else if (st === 'firestore') {
      const pid = form.fs_project || form.project_id
      if (!pid || !form.fs_database || !form.fs_collection) return
      body = { target: 'firestore', fs_project: pid, fs_database: form.fs_database, fs_collection: form.fs_collection, overwrite }
    } else return

    const confirmMsg = overwrite
      ? `로컬 뷰를 ${st === 'gcs' ? 'GCS' : 'Firestore'}로 이관합니다. 이미 존재하는 뷰도 덮어씁니다. 계속할까요?`
      : `로컬에 저장된 모든 뷰를 ${st === 'gcs' ? 'GCS' : 'Firestore'}로 이관합니다. 계속할까요?`
    if (!window.confirm(confirmMsg)) return

    setIsMigrating(true)
    setMigrateResult(null)
    try {
      const result = await api.migrate(body)
      setMigrateResult(result)
    } catch (e) {
      setMigrateResult({ status: 'error', message: e.message })
    } finally {
      setIsMigrating(false)
    }
  }

  const storageLabel = form.storage_type === 'gcs' ? 'GCS' : form.storage_type === 'firestore' ? 'Firestore' : ''

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <span className="settings-page-title">
          메타 설정
          {/* KBNL-129 후속: 모드 뱃지 제거 (사용자 결정) — 토글 active 가 모드 표시 */}
        </span>
        {isAdmin && (
          <div className="settings-mode-toggle">
            <button
              className={`mode-btn ${mode === 'user' ? 'active' : ''}`}
              onClick={() => switchMode('user')}
            >내 설정</button>
            <button
              className={`mode-btn ${mode === 'default' ? 'active' : ''}`}
              onClick={() => switchMode('default')}
            >공유 기본값</button>
          </div>
        )}
        <button className="btn-icon" onClick={onClose} title="닫기">✕</button>
      </div>

      <div className="settings-page-body">
        <div className="settings-body">
          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">GCP 연결</span>
            </div>
            <div className="section-body">

            {/* Project + Location 한 행에 (짧은 필드 2개) */}
            <div className="form-row">
              <div className="form-group form-group-half">
                <label>GCP Project ID *</label>
                <input
                  className="field"
                  value={form.project_id}
                  onChange={e => set('project_id', e.target.value)}
                  placeholder="my-gcp-project"
                />
              </div>

              <div className="form-group form-group-half">
                <label>Location</label>
                <input
                  className="field"
                  value={form.location}
                  onChange={e => set('location', e.target.value)}
                  placeholder={APP_DEFAULTS.location}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Gemini Model / 리전</label>
              {/* 자유 입력 + datalist 후보 — 조직 정책으로 목록 밖 모델만 허용된 환경 대응.
                  '확인' 은 현재 환경에서 실제 1회 호출(count_tokens)로 사용 가능 검증. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  className="field"
                  style={{ flex: 1 }}
                  list="gemini-model-candidates"
                  value={form.model_name ?? ''}
                  onChange={e => set('model_name', e.target.value)}
                  placeholder="gemini-2.5-flash"
                />
                <datalist id="gemini-model-candidates">
                  {GEMINI_MODEL_CANDIDATES.map(m => <option key={m} value={m} />)}
                </datalist>
                {/* 모델과 리전을 한 쌍으로 — 리전별 가용성이 다름(최신 모델은 'global').
                    기본: 서울(asia-northeast3) + gemini-2.5-flash. 비면 Location 값 사용. */}
                <input
                  className="field"
                  style={{ flex: 1, minWidth: 130 }}
                  value={form.vertex_location ?? ''}
                  onChange={e => set('vertex_location', e.target.value)}
                  placeholder={`${APP_DEFAULTS.location} (기본)`}
                  title="Gemini(LLM) 호출 리전 — 모델이 없는 리전이면 404. 최신 모델은 'global'. 비면 Location 값 사용"
                />
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={modelCheck.checking}
                  onClick={() => runModelCheck(form.model_name)}
                  title="현재 환경(프로젝트/조직 정책)에서 이 모델이 실제 호출 가능한지 1회 테스트"
                >{modelCheck.checking ? '확인 중…' : '✓ 확인'}</button>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={modelCheck.checkingAll}
                  onClick={runModelCheckAll}
                  title="후보 모델 전체를 병렬로 테스트해 사용 가능 여부를 표로 표시"
                >{modelCheck.checkingAll ? '전체 확인 중…' : '전체 확인'}</button>
              </div>
              {modelCheck.result && (
                <div className="form-hint" style={{ marginTop: 6 }}>
                  {modelCheck.result.ok
                    ? <span style={{ color: '#15803d' }}>✓ 사용 가능 — <code>{modelCheck.result.model}</code> ({modelCheck.result.latency_ms}ms)</span>
                    : <span style={{ color: '#b91c1c' }}>✗ 사용 불가 — {modelCheck.result.error}</span>}
                </div>
              )}
              {modelCheck.all && (
                <div className="form-hint" style={{ marginTop: 6 }}>
                  {modelCheck.all.map(r => (
                    <div key={r.model}>
                      {r.ok
                        ? <span style={{ color: '#15803d' }}>✓ {r.model} ({r.latency_ms}ms)</span>
                        : <span style={{ color: '#b91c1c' }}>✗ {r.model} — {(r.error || '').slice(0, 90)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* LLM 호출 리전(vertex_location) 입력은 Gemini Model 옆으로 승격 —
                모델·리전을 한 쌍으로 지정 (고급 설정의 중복 입력 제거). */}
            </div>
          </section>

          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">BigQuery 데이터 소스</span>
              <span className="section-meta">선택 {totalSelected}개</span>
              <span style={{ flex: 1 }} />
              {!loadingDs && form.project_id && (
                <button
                  type="button"
                  className="section-btn"
                  onClick={refreshDatasets}
                  title="데이터셋 목록 다시 조회"
                >↻</button>
              )}
              {loadingDs && <span className="section-loading">조회 중…</span>}
            </div>
            <div className="section-body">

            <div className="form-hint" style={{ marginBottom: 8 }}>
              데이터셋을 클릭하면 테이블 목록이 펼쳐집니다. 여러 데이터셋에서 테이블을 선택할 수 있습니다.
            </div>

            {datasets.length > 0 ? (
              <div className="ds-table-picker">
                {datasets.map(dsId => {
                  const isExpanded = expandedDs.has(dsId)
                  const dsTables = dsTableCache[dsId] || []
                  const dsSelected = selectedTables[dsId] || []
                  const isLoading = loadingTables[dsId]
                  const dsSelectedCount = dsSelected.length

                  return (
                    <div key={dsId} className="ds-group">
                      <div
                        className={`ds-header ${dsSelectedCount > 0 ? 'ds-has-selection' : ''}`}
                        onClick={() => toggleDatasetExpand(dsId)}
                      >
                        <span className="ds-expand-icon">{isExpanded ? '▾' : '▸'}</span>
                        <span className="ds-name">{dsId}</span>
                        {dsSelectedCount > 0 && (
                          <span className="ds-count">{dsSelectedCount}개 선택</span>
                        )}
                        {isLoading && <span className="form-loading">조회 중...</span>}
                      </div>

                      {isExpanded && dsTables.length > 0 && (
                        <div className="table-select-list">
                          <label className="table-select-item table-select-all">
                            <input
                              type="checkbox"
                              checked={dsTables.length > 0 && dsSelectedCount === dsTables.length}
                              onChange={() => toggleDatasetAll(dsId)}
                            />
                            <span>전체 선택 ({dsTables.length})</span>
                          </label>
                          {dsTables.map(t => (
                            <label key={t} className="table-select-item">
                              <input
                                type="checkbox"
                                checked={dsSelected.includes(t)}
                                onChange={() => toggleTable(dsId, t)}
                              />
                              <span>{t}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {isExpanded && !isLoading && dsTables.length === 0 && (
                        <div className="form-hint" style={{ paddingLeft: 24, paddingTop: 4 }}>
                          테이블이 없거나 조회에 실패했습니다.
                          <button className="btn-link" onClick={() => {
                            setDsTableCache(prev => { const n = {...prev}; delete n[dsId]; return n })
                            fetchTablesFor(dsId)
                          }}>다시 조회</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="form-hint">
                {form.project_id
                  ? '데이터셋 목록을 불러오는 중이거나 조회할 수 없습니다.'
                  : 'GCP Project ID를 입력하면 데이터셋 목록이 표시됩니다.'}
              </div>
            )}
            </div>
          </section>

          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">Dataplex 용어집</span>
              <span className="section-meta">선택 {(form.glossary_ids || []).length}개</span>
              <span style={{ flex: 1 }} />
              {!loadingGl && form.project_id && (
                <button
                  type="button"
                  className="section-btn"
                  onClick={fetchGlossaries}
                  title="용어집 목록 다시 조회"
                >↻</button>
              )}
              {loadingGl && <span className="section-loading">조회 중…</span>}
            </div>
            <div className="section-body">

            <div className="form-hint" style={{ marginBottom: 8 }}>
              선택한 모든 용어집을 병합하여 생성/변환에 사용합니다. 비워두면 용어집 없이 진행.
            </div>

            {glossaries.length > 0 ? (
              <div className="table-select-list" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {glossaries.map(g => {
                  const checked = (form.glossary_ids || []).includes(g.id)
                  return (
                    <label key={g.id} className="table-select-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleGlossary(g.id)}
                      />
                      <span>{g.display_name || g.id}</span>
                    </label>
                  )
                })}
              </div>
            ) : (
              <div className="form-group">
                <label>Glossary ID 직접 입력</label>
                <input
                  className="field"
                  value={(form.glossary_ids || []).join(', ')}
                  onChange={e => {
                    const ids = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    setForm(f => ({ ...f, glossary_ids: ids }))
                  }}
                  placeholder="banking-glossary, common-glossary"
                />
                <div className="form-hint">
                  Glossary 목록을 가져오지 못했습니다. 콤마로 구분해 직접 입력하세요.
                </div>
              </div>
            )}
            </div>
          </section>

          {/* KBNL-57: Virtual Schema 선택 — 환경변수로 기능 OFF 면 섹션 자체 숨김 */}
          {virtualSchemaEnabled !== false && (
            <section className="settings-section">
              <div className="section-head">
                <span className="section-title">Virtual Schema</span>
                <span className="section-meta">
                  선택 {(form.selected_virtual_schema_ids || []).length}개 / 전체 {vsList.length}
                </span>
                <span style={{ flex: 1 }} />
                {vsLoading && <span className="section-loading">조회 중…</span>}
              </div>
              <div className="section-body">
                <div className="form-hint" style={{ marginBottom: 8 }}>
                  선택한 Virtual Schema 들의 활성 버전이 SQL→SV 변환 / Semantic View 생성 / Sandbox 의 LLM 컨텍스트에 자동 주입됩니다.
                  Virtual Schema 페이지의 항목별 "✓ 사용" 토글과 동일한 설정을 공유합니다.
                </div>
                {vsList.length === 0 && !vsLoading && (
                  <div className="form-hint">
                    등록된 Virtual Schema 가 없습니다. <strong>Virtual Schema</strong> 탭에서 먼저 추가하세요.
                  </div>
                )}
                {vsList.length > 0 && (
                  <>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <input
                        className="field"
                        type="text"
                        value={vsQuery}
                        onChange={e => setVsQuery(e.target.value)}
                        placeholder="이름 / 설명 / 작성자 검색"
                      />
                    </div>
                    <div className="table-select-list" style={{ maxHeight: 240, overflowY: 'auto' }}>
                      {filteredVsList.length === 0 ? (
                        <div className="form-hint">조건에 맞는 Virtual Schema 가 없습니다.</div>
                      ) : filteredVsList.map(v => {
                        const docId = v.doc_id || `${v.owner_user_id || ''}__${v.name || ''}`
                        const checked = (form.selected_virtual_schema_ids || []).includes(docId)
                        const mine = v.owner_user_id === myUserId
                        return (
                          <label key={docId} className="table-select-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleVirtualSchema(docId)}
                            />
                            <span style={{ flex: 1 }}>
                              {v.name}
                              {v.description && (
                                <span style={{ marginLeft: 8, color: 'var(--text-dim)', fontSize: 12 }}>
                                  — {v.description}
                                </span>
                              )}
                            </span>
                            <span style={{
                              fontSize: 11,
                              color: mine ? 'var(--accent)' : 'var(--text-dim)',
                              fontWeight: mine ? 600 : 400,
                              marginLeft: 8,
                            }}>
                              {mine ? '나' : (v.owner_name || v.owner_user_id || '?')}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">뷰 저장 위치</span>
            </div>
            <div className="section-body">

            <div className="form-row">
              <div className="form-group form-group-half">
                <label>사용자별 뷰 격리 ID</label>
                <input
                  className="field"
                  value={form.user_id || ''}
                  onChange={e => set('user_id', e.target.value)}
                  placeholder="비워두면 로그인 계정으로 자동 설정"
                />
                {/* KBNL-143: 빈 값이면 서버가 로그인 계정으로 fallback — 개별 로그인
                    환경에서는 입력 불필요. 무로그인(공용 계정) 환경 전용 안내. */}
                <div className="form-hint">
                  <strong>개별 로그인을 사용하는 환경에서는 입력하지 않아도 됩니다</strong> —
                  비워두면 로그인 계정 ID로 자동 격리됩니다. 로그인 없이(공용 계정으로)
                  사용하는 환경에서만 각자 고유 ID를 직접 입력하세요.
                </div>
              </div>

              <div className="form-group form-group-half">
                <label>저장소 종류</label>
                <select className="field" value={form.storage_type || APP_DEFAULTS.storage_type} onChange={e => { set('storage_type', e.target.value); setMigrateResult(null) }}>
                  <option value="firestore">Firestore</option>
                  <option value="gcs">Google Cloud Storage</option>
                </select>
              </div>
            </div>

            {form.storage_type === 'gcs' && (
              <div className="form-group">
                <label>GCS 경로</label>
                <input
                  className="field"
                  value={form.gcs_path || ''}
                  onChange={e => set('gcs_path', e.target.value)}
                  placeholder="gs://my-bucket/semantic-views/"
                />
                <div className="form-hint">뷰 JSON 파일이 저장될 GCS 경로</div>
              </div>
            )}

            {form.storage_type === 'firestore' && (
              <>
                <div className="form-group">
                  <label>GCP Project</label>
                  <input
                    className="field"
                    value={form.fs_project || ''}
                    onChange={e => set('fs_project', e.target.value)}
                    placeholder={form.project_id || 'my-gcp-project'}
                  />
                  <div className="form-hint">비워두면 위 GCP Project ID 를 사용</div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label>
                      Database
                      {loadingFsDb && <span className="form-loading"> 조회 중…</span>}
                      {!loadingFsDb && fsDatabases.length === 0 && (
                        <button type="button" className="btn-link" onClick={fetchFsDatabases}> 다시 조회</button>
                      )}
                    </label>
                    {fsDatabases.length > 0 ? (
                      <select className="field" value={form.fs_database || APP_DEFAULTS.fs_database} onChange={e => set('fs_database', e.target.value)}>
                        {fsDatabases.map(db => (
                          <option key={db.id} value={db.id}>{db.id}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="field"
                        value={form.fs_database || ''}
                        onChange={e => set('fs_database', e.target.value)}
                        placeholder={APP_DEFAULTS.fs_database}
                      />
                    )}
                  </div>

                  <div className="form-group form-group-half">
                    <label>
                      Collection
                      {loadingFsColl && <span className="form-loading"> 조회 중…</span>}
                      {!loadingFsColl && fsCollections.length === 0 && (
                        <button type="button" className="btn-link" onClick={fetchFsCollections}> 다시 조회</button>
                      )}
                    </label>
                    {fsCollections.length > 0 ? (
                      <select className="field" value={form.fs_collection || ''} onChange={e => set('fs_collection', e.target.value)}>
                        <option value="">-- 선택 --</option>
                        {fsCollections.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="field"
                        value={form.fs_collection || ''}
                        onChange={e => set('fs_collection', e.target.value)}
                        placeholder={APP_DEFAULTS.fs_collection}
                      />
                    )}
                  </div>
                </div>

                {fsCollections.length > 0 && (
                  <div className="form-group">
                    <div className="form-hint">목록에 없으면 직접 입력:</div>
                    <input
                      className="field"
                      value={form.fs_collection || ''}
                      onChange={e => set('fs_collection', e.target.value)}
                      placeholder={APP_DEFAULTS.fs_collection}
                    />
                  </div>
                )}
              </>
            )}
            </div>
          </section>

          {SHOW_MIGRATION_TOOLS && (form.storage_type === 'gcs' || form.storage_type === 'firestore') && (
            <section className="settings-section">
              <div className="section-head">
                <span className="section-title">이관 도구</span>
              </div>
              <div className="section-body">
                <div className="form-hint" style={{ marginBottom: 8 }}>
                  로컬 서버에 저장된 뷰 파일을 위에서 선택한 {storageLabel} 로 복사합니다.
                </div>
                <div className="migrate-actions">
                  <button
                    className="btn-secondary"
                    disabled={isMigrating}
                    onClick={() => handleMigrate(false)}
                  >{isMigrating ? '이관 중…' : '이관 실행'}</button>
                  <button
                    className="btn-secondary"
                    disabled={isMigrating}
                    onClick={() => handleMigrate(true)}
                    title="이미 존재하는 뷰도 덮어쓰기"
                  >{isMigrating ? '이관 중…' : '덮어쓰기 이관'}</button>
                </div>
                {migrateResult && (
                  <div className={`migrate-result migrate-${migrateResult.status === 'error' ? 'error' : 'success'}`}>
                    <div className="migrate-result-msg">{migrateResult.message}</div>
                    {migrateResult.details && migrateResult.details.length > 0 && (
                      <ul className="migrate-result-list">
                        {migrateResult.details.map((d, i) => (
                          <li key={i}>
                            <strong>{d.name}</strong>: {d.status}
                            {d.reason && ` (${d.reason})`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      <div className="settings-page-footer">
        <button className="btn-secondary" onClick={handleResetDefaults} disabled={resetting}>
          {resetting ? '로드 중...' : '서버 기본값 초기화'}
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn-secondary" onClick={onClose}>취소</button>
        <button className="btn-primary" onClick={handleSave}>저장</button>
      </div>
    </div>
  )
}
